/**
 * Component: Widget SDK NPM entrypoint
 * SpecReferenceID: WIDGET-V1
 * Requirements: WIDGET-V1-R01, WIDGET-V1-R04, WIDGET-V1-R06,
 *               WIDGET-V1-R08, WIDGET-V1-R14, WIDGET-V1-R15,
 *               WIDGET-V1-R16, WIDGET-V1-R18
 *
 * Public entrypoint for the ``@galen/widget`` NPM package.
 * Framework-based consumers import from this module:
 *
 *     import { GALEN_WIDGET_VERSION, initGalenWidget } from '@galen/widget';
 *
 * Batch B makes the init pipeline real: version handshake,
 * config fetch, theme application, and enabled-surface
 * gating all flow through ``runInitPipeline``. The surface
 * components (``<GalenChat />``, ``<GalenSearch />`` etc.)
 * ship in Batch C.
 */

import { runInitPipeline, type WidgetRuntime } from './core/init';
import type { OnAuthRefresh } from './api/client';

/**
 * Widget SDK semver. Sourced from ``package.json`` at
 * build time via the ``__GALEN_WIDGET_VERSION__`` vite
 * ``define`` replacement so the NPM version on disk IS
 * the runtime-reported version — no separate constant
 * that can drift (WIDGET-V1-R04).
 */
declare const __GALEN_WIDGET_VERSION__: string;
export const GALEN_WIDGET_VERSION = __GALEN_WIDGET_VERSION__;

/**
 * Backend semver range the widget was built to support.
 * Passed to the R18 version handshake during init so a
 * mismatched deployment aborts before rendering.
 *
 * Kept alongside ``GALEN_WIDGET_VERSION`` so both version
 * constants are visible in one place; bumping the backend
 * range is a deliberate author action, not a build-time
 * substitution.
 */
export const GALEN_WIDGET_COMPATIBLE_BACKEND_RANGE = '^1.0.0';

/**
 * Documented options shape for the widget initialisation
 * call.
 */
export interface InitGalenWidgetOptions {
  /**
   * Base URL of the Galen deployment (e.g.
   * ``https://galen-portal.example.com``). The widget
   * resolves ``/api/widget/version`` and
   * ``/api/widget/config`` against this.
   */
  baseUrl: string;
  /**
   * JWT minted by the host backend via AUTH-V1-R04 API-key
   * exchange. Sent as ``Authorization: Bearer <token>`` on
   * every backend call.
   *
   * WIDGET-V1-R26: optional. Omit it to run the widget in
   * anonymous mode — the widget then mints a short-lived
   * ``anonymous``-role session token from the deployment's
   * public ``/api/widget/anon-session`` endpoint and uses
   * that instead (re-minting transparently on expiry).
   * Anonymous mode only works on deployments that opt in via
   * the ``WIDGET_ANONYMOUS_ACCESS_ENABLED`` server setting;
   * elsewhere, omitting ``token`` surfaces an init error.
   */
  token?: string;
  /**
   * DOM element that will host the widget; receives theme
   * CSS custom properties and, in Batch C, the rendered
   * surface components.
   */
  container: HTMLElement;
  /**
   * Optional surface the host wants to render. When
   * supplied the init pipeline asserts it is in the
   * deployment's ``enabled_surfaces`` list. Omit to only
   * initialise the runtime without mounting a surface.
   */
  surface?: string;
  /**
   * WIDGET-V1-R08: callback invoked when the backend
   * returns 401. The host should refresh the JWT and
   * return the new value; the widget retries the request
   * once.
   */
  onAuthRefresh?: OnAuthRefresh;
  /**
   * Optional callback invoked when a non-recoverable
   * error reaches the widget (version mismatch, config
   * fetch failure, backend unavailable, disabled surface,
   * auth refresh failure).
   */
  onError?: (error: Error) => void;
}

/**
 * Initialise the widget runtime.
 *
 * Runs the R18 -> R14 -> R16 -> R15 pipeline and returns
 * a ``WidgetRuntime`` handle carrying the authenticated
 * client, the resolved config, and the container element.
 * Batch C surface components accept the returned runtime
 * and mount into it.
 */
export async function initGalenWidget(
  options: InitGalenWidgetOptions,
): Promise<WidgetRuntime> {
  return runInitPipeline({
    baseUrl: options.baseUrl,
    token: options.token,
    widgetVersion: GALEN_WIDGET_VERSION,
    compatibleBackendRange: GALEN_WIDGET_COMPATIBLE_BACKEND_RANGE,
    container: options.container,
    surface: options.surface,
    onAuthRefresh: options.onAuthRefresh,
    onError: options.onError,
  });
}

// Re-export error taxonomy so consumers can instanceof
// against the documented error types without deep imports.
export {
  WidgetError,
  WidgetApiError,
  WidgetAuthError,
  WidgetNetworkError,
  WidgetRateLimitError,
} from './api/errors';
export { WidgetVersionMismatchError } from './core/version';
export { WidgetSurfaceDisabledError } from './core/surfaces';

export type { WidgetRuntime } from './core/init';
export type { WidgetConfig, WidgetConfigResponse, WidgetSurface } from './core/config';
export type { WidgetBackendVersion } from './core/version';
export type { OnAuthRefresh } from './api/client';

// Surface components + mount helper (Batch C).
export { BrowseSurface } from './surfaces/browse';
export { GalenChat } from './surfaces/chat';
export { GalenSearch } from './surfaces/galen-search';
export { SemanticSearch } from './surfaces/semantic-search';
export { SimpleSearch } from './surfaces/simple-search';
export { mountSurface, type WidgetSurfaceHandle } from './surfaces/mount';
